// Unified form component size system
export type FormComponentSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface FormSizeConfig {
  // Container/wrapper styles
  height: string;
  padding: string;
  text: string;

  // Input-specific styles
  inputPadding: string;

  // Combobox-specific styles (for buttons and icons)
  buttonHeight: string;
  buttonPadding: string;
  searchIcon: string;
  chevron: string;
  searchIconMargin: string;
  chevronMargin: string;

  // Multi-select specific
  containerMulti: string;
  paddingMulti: string;
  inputMulti: string;

  // Options/dropdown styles
  option: string;

  // Selected item styles
  selectedItemPadding: string;
}

/**
 * Gets standardized size classes for form components
 * Based on analysis of existing patterns and ensuring visual consistency
 */
export const getFormComponentSizeClasses = (
  size: FormComponentSize = 'md'
): FormSizeConfig => {
  const sizeConfig: Record<FormComponentSize, FormSizeConfig> = {
    xs: {
      height: 'h-7', // 28px - compact for dense layouts
      padding: 'px-1 py-1', // Reduced from px-2 for more compact feel
      text: 'text-xs', // 12px font
      inputPadding: 'py-1 px-1', // Match container padding
      buttonHeight: 'h-7',
      buttonPadding: 'p-1',
      searchIcon: 'h-3 w-3',
      chevron: 'h-3 w-3',
      searchIconMargin: 'ml-[1px]',
      chevronMargin: 'mr-[0.5px]',
      containerMulti: 'min-h-[28px]',
      paddingMulti: 'px-1.5 py-0.5',
      inputMulti: 'py-1 pl-1 pr-8 text-xs',
      option: 'py-1 pl-2 pr-7 text-xs',
      selectedItemPadding: 'pl-0 pr-0.5',
    },
    sm: {
      height: 'h-8', // 32px - slightly larger than xs
      padding: 'px-1.5 py-1', // Reduced from px-2.5 per user request
      text: 'text-xs', // 12px font - consistent with xs for small sizes
      inputPadding: 'py-1 px-1.5', // Match container padding
      buttonHeight: 'h-8',
      buttonPadding: 'p-1.5',
      searchIcon: 'h-3.5 w-3.5',
      chevron: 'h-3.5 w-3.5',
      searchIconMargin: 'ml-[2px]', // Reduced from ml-[5px] per user request
      chevronMargin: 'mr-[1px]', // Reduced from mr-[3px] per user request
      containerMulti: 'min-h-[32px]',
      paddingMulti: 'px-2 py-1',
      inputMulti: 'py-1 pl-1 pr-9 text-xs',
      option: 'py-1 pl-2 pr-8 text-xs',
      selectedItemPadding: 'pl-0 pr-1', // Changed from pl-1 pr-2 per user request
    },
    md: {
      height: 'h-10', // 40px - current standard, matches existing input
      padding: 'px-2 py-1.5', // 8px horizontal, 6px vertical (updated to match multi-select)
      text: 'text-sm', // 14px font (current standard)
      inputPadding: 'py-1.5 px-2', // Match container padding
      buttonHeight: 'h-10',
      buttonPadding: 'p-2',
      searchIcon: 'h-[17px] w-[17px]',
      chevron: 'h-[14px] w-[14px]',
      searchIconMargin: 'ml-[5px]',
      chevronMargin: 'mr-[3px]',
      containerMulti: '',
      paddingMulti: 'px-2 py-1',
      inputMulti: 'py-2 pl-1 pr-10 text-sm',
      option: 'py-1.5 pl-3 pr-9 text-sm',
      selectedItemPadding: 'pl-1.5 pr-1',
    },
    lg: {
      height: 'h-12', // 48px - larger for prominent forms
      padding: 'px-4 py-2', // 16px horizontal, 8px vertical
      text: 'text-base', // 16px font - larger text for accessibility
      inputPadding: 'py-2 px-4', // Match container padding
      buttonHeight: 'h-12',
      buttonPadding: 'p-3',
      searchIcon: 'h-5 w-5',
      chevron: 'h-5 w-5',
      searchIconMargin: 'ml-[6px]',
      chevronMargin: 'mr-[4px]',
      containerMulti: 'min-h-[48px]',
      paddingMulti: 'px-3 py-1.5',
      inputMulti: 'py-2.5 pl-1.5 pr-12 text-base',
      option: 'py-2 pl-4 pr-10 text-base',
      selectedItemPadding: 'pl-2 pr-1.5',
    },
    xl: {
      height: 'h-14', // 56px - extra large for hero forms
      padding: 'px-5 py-2.5', // 20px horizontal, 10px vertical
      text: 'text-lg', // 18px font - prominent text
      inputPadding: 'py-2.5 px-5', // Match container padding
      buttonHeight: 'h-14',
      buttonPadding: 'p-4',
      searchIcon: 'h-6 w-6',
      chevron: 'h-6 w-6',
      searchIconMargin: 'ml-[7px]',
      chevronMargin: 'mr-[5px]',
      containerMulti: 'min-h-[56px]',
      paddingMulti: 'px-4 py-2',
      inputMulti: 'py-3 pl-2 pr-14 text-lg',
      option: 'py-2.5 pl-5 pr-12 text-lg',
      selectedItemPadding: 'pl-2.5 pr-2',
    },
  };

  return sizeConfig[size];
};

/**
 * Common form component base styles that should be consistent across all form components
 */
export const FORM_COMPONENT_BASE_STYLES = {
  // Border and background
  border: 'border border-slate-200 rounded-lg bg-background',

  // Focus states - unified across all form components
  focus:
    'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',

  // Focus-visible for keyboard-only focus indication (preferred for most interactive elements)
  focusVisible:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',

  // Disabled states
  disabled: 'disabled:cursor-not-allowed disabled:opacity-50',
  disabledContainer: 'opacity-50 bg-slate-50 cursor-not-allowed',

  // Placeholder styling - standardized color
  placeholder: 'placeholder:text-slate-500',

  // Input text color
  text: 'text-slate-800',

  // Spacing
  margin: 'mt-2',

  // Transitions
  transition: 'transition-colors duration-200 ease-in-out',
} as const;

/**
 * Gets the complete class string for a form component
 */
export const getFormComponentClasses = (
  size: FormComponentSize = 'md',
  additionalClasses?: string
) => {
  const sizeClasses = getFormComponentSizeClasses(size);
  const baseClasses = [
    FORM_COMPONENT_BASE_STYLES.border,
    FORM_COMPONENT_BASE_STYLES.focus,
    FORM_COMPONENT_BASE_STYLES.text,
    FORM_COMPONENT_BASE_STYLES.margin,
    FORM_COMPONENT_BASE_STYLES.transition,
    sizeClasses.height,
    sizeClasses.padding,
    sizeClasses.text,
  ].join(' ');

  return additionalClasses
    ? `${baseClasses} ${additionalClasses}`
    : baseClasses;
};
